/**
 * The ONE place that decides "is this a phone-shaped viewport", plus the pure
 * geometry the layout has to satisfy.
 *
 * Every responsive rule in the game used to key off WIDTH alone (`max-width:900px`
 * for the compact HUD, `max-width:640px` for the bottom sheet). A landscape phone
 * — 844×390 — is WIDE and SHORT, so it matched nothing: it kept a 114px header +
 * nav stack on a 390px-tall screen, a bottom-centred hotbar clipped by the edge,
 * and centred modal cards taller than the space they were given. Height is the
 * dimension that actually hurts, so it gets a predicate of its own.
 *
 * The rect helpers are here rather than in the harness so the contract ("nothing
 * overlaps, nothing is cut off, fingers get 40px") is a unit test rather than a
 * thing someone eyeballs on a phone.
 */

export interface Rect { left: number; top: number; right: number; bottom: number; }

/** Landscape phone / very short window: the header+nav stack must collapse. */
export const SHORT_VIEWPORT_MAX_H = 560;
/** Narrow window / phone: the HUD collapses (hotbar to six slots, panels dock). */
export const COMPACT_HUD_MAX_W = 900;
/** Minimum comfortable finger target. The auth screens set this precedent. */
export const MIN_TAP_PX = 40;

function mq(query: string): boolean {
  return typeof matchMedia === 'function' && matchMedia(query).matches;
}

/** Pure form, so tests can assert the breakpoint without a layout engine. */
export function isShortViewportAt(height: number): boolean {
  return height <= SHORT_VIEWPORT_MAX_H;
}

/** Live form — a short viewport is the one that needs the chrome folded away. */
export function isShortViewport(): boolean {
  return mq(`(max-height: ${SHORT_VIEWPORT_MAX_H}px)`);
}

export function isCompactHudAt(width: number): boolean {
  return width <= COMPACT_HUD_MAX_W;
}

/**
 * "Phone layout" = either dimension is phone-sized. A 844×390 landscape phone is
 * compact by width AND short by height; a 390×844 portrait phone is compact by
 * width only; a 1200×400 short desktop window is short only. All three want the
 * single-bottom-band layout, so they share one predicate.
 */
export function isPhoneLayoutAt(width: number, height: number): boolean {
  return isCompactHudAt(width) || isShortViewportAt(height);
}

export function isPhoneLayout(): boolean {
  return mq(`(max-width: ${COMPACT_HUD_MAX_W}px), (max-height: ${SHORT_VIEWPORT_MAX_H}px)`);
}

/** Subscribe to either breakpoint flipping. Returns an unsubscribe. */
export function onLayoutChange(fn: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {};
  const list = [
    matchMedia(`(max-width: ${COMPACT_HUD_MAX_W}px)`),
    matchMedia(`(max-height: ${SHORT_VIEWPORT_MAX_H}px)`),
  ];
  const h = () => fn();
  for (const m of list) m.addEventListener('change', h);
  return () => { for (const m of list) m.removeEventListener('change', h); };
}

// ─── Geometry the layout must satisfy (used by tests) ────────────────────────

/** Zero-area rects never overlap: a hidden element measures 0×0 and is not a bug. */
export function isEmptyRect(r: Rect): boolean {
  return r.right - r.left <= 0 || r.bottom - r.top <= 0;
}

/** True when two visible rects share any area. Touching edges do NOT count. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  if (isEmptyRect(a) || isEmptyRect(b)) return false;
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/** Overlapping area in px² — a size, so "1px of anti-aliasing" reads differently to "half the hotbar". */
export function overlapArea(a: Rect, b: Rect): number {
  if (!rectsOverlap(a, b)) return 0;
  return (Math.min(a.right, b.right) - Math.max(a.left, b.left))
    * (Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
}

/** Fully inside the viewport box — anything else is clipped by an edge. */
export function insideViewport(r: Rect, vw: number, vh: number, tol = 1): boolean {
  if (isEmptyRect(r)) return true;                    // not shown → not cut off
  return r.left >= -tol && r.top >= -tol && r.right <= vw + tol && r.bottom <= vh + tol;
}

// ─── The right-hand thumb rail ───────────────────────────────────────────────
/**
 * The stack that sits on the action button's own edge: [Build/Village] over
 * [map toggle] over [ACTION]. The CSS twin is `--tb-rail-*` in app.css; the
 * numbers live here too because "the map toggle does not overlap the action
 * button at 390x844" is a claim worth a unit test rather than a phone in a hand.
 */
export const ACTION_BTN_PX = 72;   // farm.ts inline size
export const RAIL_GAP_PX = 8;
export const BAND_PAD_PX = 6;      // .tb-bottom-band padding-bottom floor
export const RAIL_RIGHT_PX = 8;    // the band's px-2, so the column lines up

/**
 * Rect of rail slot `i` (0 = map toggle, 1 = Build/Village), counting UPWARD from
 * the action button. `vh` is the viewport height; the band overlays the world, so
 * the play area's bottom edge is the viewport's.
 */
export function railSlotRect(i: number, vw: number, vh: number, w: number, h = MIN_TAP_PX): Rect {
  const bottom = vh - (BAND_PAD_PX + ACTION_BTN_PX + RAIL_GAP_PX + i * (MIN_TAP_PX + RAIL_GAP_PX));
  const right = vw - RAIL_RIGHT_PX;
  return { left: right - w, top: bottom - h, right, bottom };
}

/** The action button itself — the rail's anchor, right-aligned in the band. */
export function actionButtonRect(vw: number, vh: number): Rect {
  const right = vw - RAIL_RIGHT_PX;
  const bottom = vh - BAND_PAD_PX;
  return { left: right - ACTION_BTN_PX, top: bottom - ACTION_BTN_PX, right, bottom };
}

/** A finger has to hit it: both axes at least MIN_TAP_PX. */
export function isTapTargetOk(r: Rect, min = MIN_TAP_PX): boolean {
  if (isEmptyRect(r)) return true;                    // hidden controls are not targets
  return (r.right - r.left) >= min - 0.5 && (r.bottom - r.top) >= min - 0.5;
}
